import React, { useEffect, useMemo, useRef, useState } from 'react';
import { __, sprintf } from '@wordpress/i18n';
import { useParams } from 'react-router-dom';
import { head, isEmpty, pathOr } from 'ramda';

// tools
import getNumberWithCurrency from '../../helpers/getNumberWithCurrency';

// store
import { storeSet, useStoreValue } from '../../store';

// components
import ProjectFeaturedImage from './components/ProjectFeaturedImage';
import ProjectInfo from './components/ProjectInfo';
import Button3d from '../../components/ButtonWrapper/components/Button3d';
import ButtonFlat from '../../components/ButtonWrapper/components/ButtonFlat';
import ButtonFlatAccent from '../../components/ButtonWrapper/components/ButtonFlatAccent';
import DateRangeBar from '../../components/DateRangeBar';
import NumericProgressBar from '../../components/NumericProgressBar';
import WidgetTeam from '../../components/WidgetTeam';
import CrossedEyeIcon from '../../icons/CrossedEyeIcon';
import WidgetEarningsTotal from './components/WidgetEarningsTotal';
import ModalAddCueSheetItem from '../../components/ModalAddCueSheetItem';
import ModalEditCueSheetItem from '../../components/ModalEditCueSheetItem';
import ModalBulkEditCueSheetItems from '../../components/ModalBulkEditCueSheetItems';
import ButtonFlatWarning from '../../components/ButtonWrapper/components/ButtonFlatWarning';
import ButtonGroup from '../../components/ButtonGroup';
import ProjectCueSheet from './components/ProjectCueSheet';
import ProjectCalendar from './components/ProjectCalendar';
import CommCenterProject from './components/CommCenterProject';
import useCacheProjectAudio from '../../hooks/useCacheProjectAudio';
import useSelection from '../../hooks/useSelection';
import { SELECTION_KINDS } from '../../helpers/selection';

const Project = () => {
    const projects = useStoreValue('projects');
    const cueSheetPanel = useStoreValue('cueSheetPanel');
    const cueSheet = useStoreValue('cueSheet');
    const { projectId } = useParams();
    const [data, setData] = useState({});
    const [isHidCompleted, setIsHidCompleted] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [deleteArmed, setDeleteArmed] = useState(false);
    const deleteArmedTimer = useRef(null);

    // The cue sheet rows write their selection here; the Comm Center reads the
    // same value to scope its feed, so the toolbar has nothing of its own.
    const { selectedIds, clear: clearSelection } = useSelection(SELECTION_KINDS.CUE);

    const selectedCueSheetItems = useMemo(
        () => cueSheet.filter(item => selectedIds.includes(item.id)),
        [cueSheet, selectedIds]
    );
    const selectedCount = selectedCueSheetItems.length;

    const handleCueSheetPanel = (type) => {
        storeSet('cueSheetPanel', type);
    }

    // Same two-step confirmation as the delete button of a single row: the first
    // click arms it, a second one within four seconds goes through.
    const handleBulkDelete = async () => {
        if (!deleteArmed) {
            setDeleteArmed(true);
            deleteArmedTimer.current = setTimeout(() => setDeleteArmed(false), 4000);
            return;
        }

        clearTimeout(deleteArmedTimer.current);
        setDeleteArmed(false);

        try {
            await storeSet('bulkDeleteCueSheetItems', projectId, selectedIds);
        } finally {
            clearSelection();
        }
    };

    // Switching tab — or project — shows a different set of rows, so a selection
    // made on the previous one would linger invisible behind the toolbar.
    useEffect(() => {
        clearSelection();
        setDeleteArmed(false);
    }, [cueSheetPanel, projectId, clearSelection]);

    useEffect(() => () => clearTimeout(deleteArmedTimer.current), []);

    useEffect(() => {
        const project = pathOr({}, [], head(projects.filter(o => o.id === projectId)));
        setData(project);

        // Fetch project team via React Query action
        storeSet('fetchProjectTeam', projectId);
    }, [projectId, projects]);

    useEffect(() => {
        // Opening a project clears its "new comunicazioni" badge
        storeSet('markProjectSeen', projectId);
    }, [projectId]);

    useCacheProjectAudio(projectId);

    return (
        !isEmpty(data)
            ? <>
                <div className="appDashboard">
                    <div className="appDashboard__workbench">
                        <div className="projectInfoWrapper">
                            <ProjectFeaturedImage url={data.thumbnailUrl} title={data.title}/>
                            <ProjectInfo data={data}/>
                        </div>
                        <div className="projectDataWrapper">
                            <div className="appWorkbenchPanel projectDataPanel">
                                <div>
                                    <ButtonGroup>
                                        <Button3d
                                            changeFn={() => handleCueSheetPanel('music')}
                                            name="music"
                                            checked={cueSheetPanel === 'music'}>
                                            {__('Cue Sheet', 'soundbetween')}
                                        </Button3d>
                                        <Button3d
                                            changeFn={() => handleCueSheetPanel('sfx')}
                                            name="sfx"
                                            checked={cueSheetPanel === 'sfx'}>
                                            {__('SFX', 'soundbetween')}
                                        </Button3d>
                                        <Button3d
                                            changeFn={() => handleCueSheetPanel('mix')}
                                            name="mix"
                                            checked={cueSheetPanel === 'mix'}>
                                            {__('Mix', 'soundbetween')}
                                        </Button3d>
                                    </ButtonGroup>
                                    <Button3d
                                        changeFn={() => handleCueSheetPanel('calendar')}
                                        name="calendar"
                                        checked={cueSheetPanel === 'calendar'}>
                                        {__('Calendar', 'soundbetween')}
                                    </Button3d>
                                </div>
                                <div>
                                    <Button3d
                                        changeFn={() => setIsHidCompleted(!isHidCompleted)}
                                        name="hideCompleted"
                                        checked={isHidCompleted}>
                                        <CrossedEyeIcon/>
                                    </Button3d>
                                    <span className="appFadedText">
                                        {__('Nascondi task completate', 'soundbetween')}
                                    </span>
                                    {selectedCount > 0
                                        ? <ButtonFlatAccent onClick={() => setIsEditModalOpen(true)}>
                                            {selectedCount > 1
                                                ? sprintf(__('Modifica (%d)', 'soundbetween'), selectedCount)
                                                : __('Modifica', 'soundbetween')}
                                        </ButtonFlatAccent>
                                        : <ButtonFlat disabled>
                                            {__('Modifica', 'soundbetween')}
                                        </ButtonFlat>
                                    }
                                    {selectedCount > 0
                                        ? <ButtonFlatWarning onClick={handleBulkDelete}>
                                            {deleteArmed
                                                ? __('Clicca di nuovo per confermare', 'soundbetween')
                                                : sprintf(__('Elimina (%d)', 'soundbetween'), selectedCount)}
                                        </ButtonFlatWarning>
                                        : null}
                                    <ModalAddCueSheetItem projectId={projectId} type={cueSheetPanel}/>
                                </div>
                            </div>
                            <div className="projectsTasksWrapper">
                                {cueSheetPanel === 'calendar'
                                    ? <ProjectCalendar projectId={projectId}/>
                                    : <ProjectCueSheet
                                        projectId={projectId}
                                        type={cueSheetPanel}
                                    />
                                }
                            </div>
                        </div>
                    </div>
                    <CommCenterProject/>
                </div>
                <div className="appDashboard__widgets">
                    <WidgetEarningsTotal/>
                    <div className="appDashboard__widget">
                        {data && data.creationDate && data.deadline
                            ? <DateRangeBar
                                startDate={data.creationDate}
                                endDate={data.deadline}
                                title="Timeline"
                                description="{t1} giorni alla consegna"
                            /> : null}
                        {data
                            ? <NumericProgressBar
                            maxValue={data.budget}
                            targetValues={[0]}
                            title="Budget speso"
                            description={`{t1} € di ${getNumberWithCurrency(data.budget)}`}
                        /> : null}
                    </div>
                    <div className="appDashboard__widget">
                        <WidgetTeam projectId={projectId}/>
                    </div>
                </div>
                <ModalEditCueSheetItem
                    visible={isEditModalOpen && selectedCount === 1}
                    onHide={() => setIsEditModalOpen(false)}
                    projectId={projectId}
                    item={selectedCueSheetItems[0] ?? null}
                />
                <ModalBulkEditCueSheetItems
                    visible={isEditModalOpen && selectedCount > 1}
                    onHide={() => setIsEditModalOpen(false)}
                    projectId={projectId}
                    items={selectedCueSheetItems}
                />
            </> : null
    )
}

export default Project;